/**
 * Pure helpers for building the next navigation destination ({ url, query })
 * for the two OracleSidebar click handlers (new-conversation and divination).
 *
 * AI-821: divination entry screens (tarot / oracle_cards / horoscope) live at
 * sub-paths like `/aichat/conversation/tarot` rather than on a
 * `?divinationEntry=` query param, so these helpers return both a `url` and a
 * `query` object.
 */
import { DIVINATION_ENTRIES, type DivinationEntry } from './SidebarDivinationButtons'

export type { DivinationEntry }

export interface NavigationDestination {
  url: string
  query: Record<string, unknown>
}

const omitChatSessionId = (
  query: Record<string, unknown>,
): Record<string, unknown> => {
  const next = { ...query }
  delete next.chatSessionId
  return next
}

export const buildDivinationDestination = (
  currentQuery: Record<string, unknown>,
  basePath: string,
  entry: DivinationEntry,
): NavigationDestination => ({
  url: `${basePath}/${entry}`,
  query: omitChatSessionId(currentQuery),
})

export const buildNewConversationDestination = (
  currentQuery: Record<string, unknown>,
  basePath: string,
  personalityId: string,
): NavigationDestination => ({
  url: basePath,
  query: {
    ...omitChatSessionId(currentQuery),
    oraclePersonalityId: personalityId,
  },
})

export const parseDivinationEntry = (
  pathname: string,
  basePath: string,
): DivinationEntry | null => (
  DIVINATION_ENTRIES.find((entry) => pathname === `${basePath}/${entry}`) ?? null
)
